/**
 * useCommitmentStates — loads the derived announced/committed overlay for a
 * set of commitments on a plan cycle (D1).
 *
 * PRD: docs/9-exploration/DISPLACEMENT-AND-COMMITMENT-STATES-PRD.md.
 *
 * Returns the empty result (and fetches nothing) when the gate is off, when
 * there is no cycle to key claims on, or when nothing is in scope — so callers
 * can render the hook unconditionally and simply get no badges.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  EMPTY_COMMITMENT_STATES,
  loadCommitmentStatesForCycle,
  type CommitmentStatesForCycle,
} from '../lib/commitmentStateService';

export interface UseCommitmentStatesInput {
  enabled: boolean;
  planCycleId: string | null | undefined;
  itemIds: readonly string[];
  workspaceId: string | null | undefined;
  defaultWorkUnitHours: number;
  threshold?: number;
}

export interface UseCommitmentStatesResult extends CommitmentStatesForCycle {
  loading: boolean;
  /** True when a cycle + scope were available and the load completed. */
  available: boolean;
}

export function useCommitmentStates(
  input: UseCommitmentStatesInput
): UseCommitmentStatesResult {
  const { enabled, planCycleId, workspaceId, defaultWorkUnitHours, threshold } = input;

  // Identity of the scope, not the array — callers rebuild the list every render.
  const itemKey = useMemo(
    () => [...new Set(input.itemIds)].sort().join(','),
    [input.itemIds]
  );

  const [state, setState] = useState<CommitmentStatesForCycle>(EMPTY_COMMITMENT_STATES);
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    const itemIds = itemKey ? itemKey.split(',') : [];
    if (!enabled || !planCycleId || itemIds.length === 0) {
      setState(EMPTY_COMMITMENT_STATES);
      setAvailable(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void loadCommitmentStatesForCycle({
      planCycleId,
      itemIds,
      workspaceId,
      defaultWorkUnitHours,
      threshold,
    })
      .then((result) => {
        if (cancelled) return;
        setState(result);
        setAvailable(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          ...EMPTY_COMMITMENT_STATES,
          error: err instanceof Error ? err.message : String(err),
        });
        setAvailable(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, planCycleId, itemKey, workspaceId, defaultWorkUnitHours, threshold]);

  return { ...state, loading, available };
}
